import * as SliderPrimitive from "@radix-ui/react-slider";
import { useId, type ReactNode } from "react";

export interface SliderProps {
  label: ReactNode;
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Readout next to the label (`[data-output]`); defaults to the raw value. */
  formatValue?: (value: number) => string;
  /** End labels under the track, e.g. "Keyword" ↔ "Meaning". */
  startLabel?: ReactNode;
  endLabel?: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
  className?: string;
}

/** Radix single-thumb slider with label, mono value output and end labels. */
export function Slider({
  label,
  value,
  onValueChange,
  min = 0,
  max = 100,
  step = 1,
  formatValue = String,
  startLabel,
  endLabel,
  hint,
  disabled,
  className,
}: SliderProps) {
  const id = useId();
  const labelId = `${id}-label`;
  const hintId = hint ? `${id}-hint` : undefined;
  const hasEnds = Boolean(startLabel ?? endLabel);
  const endsId = hasEnds ? `${id}-ends` : undefined;
  const describedBy = [endsId, hintId].filter(Boolean).join(" ") || undefined;
  const text = formatValue(value);
  return (
    <div className={className}>
      <div className="flex items-center justify-between">
        <span id={labelId} className="label mb-0">
          {label}
        </span>
        <output aria-hidden="true" className="font-mono text-xs font-semibold text-fg">
          {text}
        </output>
      </div>
      <SliderPrimitive.Root
        value={[value]}
        onValueChange={(next) => {
          const first = next[0];
          if (first !== undefined) onValueChange(first);
        }}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        className="relative mt-2 flex h-6 w-full touch-none select-none items-center data-[disabled]:opacity-50"
      >
        <SliderPrimitive.Track className="relative h-1.5 grow overflow-hidden rounded-pill bg-sunken">
          <SliderPrimitive.Range className="absolute h-full rounded-pill bg-primary" />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          aria-labelledby={labelId}
          aria-describedby={describedBy}
          aria-valuetext={text}
          className="block size-5 cursor-grab rounded-pill border-2 border-primary bg-surface shadow-xs outline-none focus-visible:ring-3 focus-visible:ring-primary/30"
        />
      </SliderPrimitive.Root>
      {hasEnds ? (
        <div id={endsId} aria-hidden="true" className="flex justify-between text-[11px] text-fg-subtle">
          <span>{startLabel}</span>
          <span>{endLabel}</span>
        </div>
      ) : null}
      {hint ? (
        <p id={hintId} className="hint">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
